import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { X, CircleNotch, PaperPlaneTilt } from "@phosphor-icons/react";
import { AxiosError } from "axios";
import { createProjectReview, MyProjectReview } from "./api";
import { MarkdownRenderer } from "../../components/MyMarkdown";

export default function ProjectReviewModal({
  projectId, latest, onClose,
}: { projectId: number; latest: MyProjectReview | null; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [repoUrl, setRepoUrl] = useState(latest?.repo_url ?? "");
  const [note, setNote] = useState("");
  const [err, setErr] = useState("");

  const mutation = useMutation({
    mutationFn: createProjectReview,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["myProjectReviews"] }),
    onError: (e: AxiosError<{ message?: string }>) =>
      setErr(e.response?.data?.message || "Bir hata oluştu, tekrar dene."),
  });

  const [reapply, setReapply] = useState(false);
  const formVisible = (!latest || latest.status === "rejected" || reapply) && !mutation.isSuccess;

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-surface-1 border border-white/10 rounded-2xl w-full max-w-lg max-h-[80vh] overflow-y-auto p-5"
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-white font-semibold">Proje İncelemesi</h3>
          <button onClick={onClose} className="text-white/30 hover:text-white/60 cursor-pointer"><X size={18} /></button>
        </div>

        {mutation.isSuccess && (
          <p className="text-myGreen text-sm bg-myGreen/10 rounded-xl px-4 py-3">
            Talebin alındı 🎉 Eğitmenin projeni inceleyecek — yanıt <strong>2-3 iş günü</strong> sürebilir; sonucu burada göreceksin. Takıldığın yer olursa eğitmenine de danışabilirsin.
          </p>
        )}

        {!mutation.isSuccess && latest?.status === "pending" && (
          <p className="text-white/50 text-sm bg-white/[0.04] rounded-xl px-4 py-3">
            İncelemede ⏳ — eğitmen yorumu <strong className="text-white/70">2-3 iş günü</strong> içinde burada görünecek.
          </p>
        )}

        {!mutation.isSuccess && latest?.status === "rejected" && !reapply && (
          <div className="text-sm bg-red-500/10 text-red-300 rounded-xl px-4 py-3 mb-3">
            Talebin işleme alınamadı: {latest.reject_reason || "—"}
            <div className="text-white/40 mt-1 text-xs">Düzeltip aşağıdan tekrar gönderebilirsin.</div>
          </div>
        )}

        {!mutation.isSuccess && latest?.status === "answered" && (
          <div className="mb-4">
            <div className="text-xs uppercase tracking-wider text-white/30 mb-2">Eğitmen İncelemesi</div>
            <div className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-4 text-sm">
              <MarkdownRenderer content={latest.review_md || ""} allowHtml={false} breaks />
            </div>
            <div className="text-white/25 text-xs mt-2">
              {latest.answered_at && new Date(latest.answered_at).toLocaleDateString("tr-TR", { day: "numeric", month: "long" })}
            </div>
            {!reapply && (
              <button onClick={() => setReapply(true)}
                className="mt-3 text-xs px-3 py-1.5 rounded-lg bg-white/[0.04] text-white/50 hover:bg-white/10 hover:text-white/80 transition-colors cursor-pointer">
                Tekrar İncelet 🔄
              </button>
            )}
          </div>
        )}

        {formVisible && (
          <form className="space-y-3" onSubmit={(e) => {
            e.preventDefault(); setErr("");
            mutation.mutate({ project_id: projectId, repo_url: repoUrl, note: note || undefined });
          }}>
            <input required value={repoUrl} onChange={(e) => setRepoUrl(e.target.value)}
              placeholder="GitHub repo linki *  (https://github.com/kullanici/repo)"
              className="w-full bg-white/[0.04] border border-white/[0.08] rounded-xl px-4 py-2.5 text-sm text-white placeholder:text-white/30 outline-none focus:border-white/20" />
            <textarea value={note} rows={4} onChange={(e) => setNote(e.target.value)}
              placeholder="Neler yaptın? Yaklaşımın, verdiğin kararlar, karşılaştığın zorluklar, özellikle neye bakmamızı istersin... (önerilir)"
              className="w-full bg-white/[0.04] border border-white/[0.08] rounded-xl px-4 py-2.5 text-sm text-white placeholder:text-white/30 outline-none focus:border-white/20 resize-none" />
            <p className="text-white/30 text-xs">Repo'nun <strong className="text-white/50">public</strong> olduğundan emin ol — değilse inceleyemeyiz.</p>
            {err && <p className="text-red-400 text-xs">{err}</p>}
            <button type="submit" disabled={mutation.isPending}
              className="w-full flex items-center justify-center gap-2 bg-myGreen/90 hover:bg-myGreen disabled:opacity-40 text-black font-semibold rounded-xl py-2.5 text-sm transition-colors cursor-pointer">
              {mutation.isPending ? <CircleNotch size={16} className="animate-spin" /> : <PaperPlaneTilt size={16} />}
              İncelemeye Gönder
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
